'use client';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type UserRole = 'subscriber' | 'provider' | 'manager' | 'admin';

export interface AuthUser {
  name: string;
  email: string;
  role: UserRole;
  initials: string;
  roleLabel: string;
  supabaseId?: string; // Supabase auth.users UUID — stored at login, available everywhere
}

interface AuthContextValue {
  user: AuthUser | null;
  setUser: (user: AuthUser | null) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  setUser: () => {},
  logout: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<AuthUser | null>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('raketnow_user');
      if (stored) {
        setUserState(JSON.parse(stored));
      }
    } catch {
      // ignore
    }
  }, []);

  const setUser = (u: AuthUser | null) => {
    setUserState(u);
    if (u) {
      localStorage.setItem('raketnow_user', JSON.stringify(u));
    } else {
      localStorage.removeItem('raketnow_user');
    }
  };

  const logout = () => {
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
